// Hardware form model: field state, type rules and API payload builders,
// shared by the add page (HardwareForm) and the device page.
import type {
  Logger,
  LoggerCreate,
  LoggerUpdate,
  TemptrackDiscoveryItem,
  RegisterLoggerPayload,
} from '../../services';
import { LOGGER_FREQUENCY_OPTIONS } from './hardwareDisplay';

export type Mode = 'view' | 'edit' | 'create';

// Coarse hardware categories — mirrors the legacy GetHardwareTypeListJSON
// dropdown (OperationHandler.aspx.cs:3991). The user picks one of these;
// the fine LoggerInfo.Type code (01-09) is derived later (from the device
// serial prefix on the backend for TempTrack registrations).
export const HW_LOGGER = 'loggers';
export const HW_ROUTER = 'routers';
export const HW_GATEWAY = 'gateways';
export const HW_WEATHER = 'weather';
export const HW_TANK = 'tank';
// ConXenviro (fine Type '10') is not in legacy. It registers exactly like a
// logger (TempTrack `loggers` segment) but is its own dropdown entry.
export const HW_CONXENVIRO = 'conxenviro';

export const HARDWARE_TYPES: { value: string; label: string; icon: string }[] = [
  { value: HW_LOGGER, label: 'Logger', icon: 'Agent' },
  { value: HW_CONXENVIRO, label: 'ConXenviro', icon: 'ConXenviro' },
  { value: HW_ROUTER, label: 'Extend', icon: 'Router' },
  { value: HW_GATEWAY, label: 'Gateway', icon: 'Gateway' },
  { value: HW_WEATHER, label: 'Weather Station', icon: 'Weather Station' },
  { value: HW_TANK, label: 'Tank', icon: 'Tank' },
];

// Reverse of PageBase.GetHardwareType: map a stored fine Type code (01-10)
// back to its coarse category, for display + field-visibility in
// view/edit mode (where formData.Type holds the loaded fine code).
export const fineTypeToCoarse = (fine: string): string => {
  switch (fine) {
    case '01': case '02': case '03': case '04': case '09': return HW_LOGGER;
    case '05': return HW_ROUTER;
    case '06': return HW_GATEWAY;
    case '07': return HW_WEATHER;
    case '08': return HW_TANK;
    case '10': return HW_CONXENVIRO;
    default: return '';
  }
};

// Coarse categories that register through TempTrack, mapped to its REST URL
// segment. ConXenviro registers as a logger.
export type TemptrackDeviceType = 'loggers' | 'gateways' | 'routers';
export const getTemptrackDeviceType = (coarse: string): TemptrackDeviceType | undefined => {
  if (coarse === HW_LOGGER || coarse === HW_CONXENVIRO) return 'loggers';
  if (coarse === HW_ROUTER) return 'routers';
  if (coarse === HW_GATEWAY) return 'gateways';
  return undefined;
};

// Field-set predicates, keyed off the coarse category (matches legacy
// LoggerAdd.aspx show/hide logic):
//  - logger fields (SIM / access / service / frequency): non-weather devices
//  - weather fields: weather only
//  - calibration row: loggers ("Calibration Frequency") + tank ("Default Temperature")
export const hasLoggerFields = (coarse: string): boolean =>
  coarse === HW_LOGGER || coarse === HW_CONXENVIRO ||
  coarse === HW_ROUTER || coarse === HW_GATEWAY;
export const hasWeatherFields = (coarse: string): boolean => coarse === HW_WEATHER;
export const hasCalibrationRow = (coarse: string): boolean =>
  coarse === HW_LOGGER || coarse === HW_CONXENVIRO || coarse === HW_TANK;

// Derive the fine LoggerInfo.Type for the MANUAL create path (no TempTrack
// discovery). For loggers we read the serial prefix exactly like the
// backend; routers/gateways/weather/tank are fixed.
export const deriveFineType = (coarse: string, serial: string): string => {
  if (coarse === HW_CONXENVIRO) return '10';
  if (coarse === HW_ROUTER) return '05';
  if (coarse === HW_GATEWAY) return '06';
  if (coarse === HW_WEATHER) return '07';
  if (coarse === HW_TANK) return '08';
  // loggers — mirror the backend serial-prefix switch
  const prefix = (serial || '').slice(0, 2).toUpperCase();
  const map: Record<string, string> = {
    WS: '07', AY: '06', BF: '05', BG: '04', BD: '02', BE: '03', AU: '10',
  };
  return map[prefix] || '01';
};

// Company is who has the device: the buyer (For Sale) or the hirer (For
// Hire). Blank or HDTECH means it isn't assigned to anyone yet.
export const HDTECH_COMPANY_ID = '1';
export const isHire = (v: string): boolean => v.toLowerCase().includes('hire');

export interface CompanyOption {
  id: string;
  name: string;
}

// Logger reporting intervals as stored in LoggerInfo.LoggerFrequency:
// TempTrack TimeInterval names, the same values the legacy app writes.
export const LOGGER_FREQUENCIES = LOGGER_FREQUENCY_OPTIONS;

// Spreadsheet field rules by fine LoggerInfo.Type.
// ConXagent only: access/service codes, SIM card, mode.
export const isAgentType = (fine: string): boolean => fine === '01';
// Scheduled loggers: logging frequency and calibration.
export const isScheduledLoggerType = (fine: string): boolean =>
  ['01', '02', '03', '04', '10'].includes(fine);
export const isWeatherType = (fine: string): boolean => fine === '07';
// The weather job polls only ZENTRA serials, which start with "A" (e.g.
// A4101946): a station with any other serial gets no readings.
export const isPolledWeatherSerial = (serial: string): boolean => /^a/i.test(serial.trim());
export const isTankType = (fine: string): boolean => fine === '08';

export const SUBSCRIPTION_OPTIONS: { value: string; label: string }[] = [
  { value: '3', label: '3 months' },
  { value: '6', label: '6 months' },
  { value: '12', label: '12 months' },
];

export const CALIBRATION_FREQUENCY_OPTIONS: { value: string; label: string }[] = [
  { value: '12', label: '12 months' },
  { value: '18', label: '18 months' },
  { value: '24', label: '24 months' },
];

export const SIM_STATUS_OPTIONS = ['Active', 'In-Active', 'Retired'];
export const SIM_TYPE_OPTIONS = ['STD-CAT-M1', 'Z5-CAT-M1', 'STD-Local', 'STD-Global', 'Not Applicable'];

/** date-input value + months, as YYYY-MM-DD (clamped to month end). */
export const addMonthsToDateInput = (value: string, months: string): string => {
  const m = Number(months);
  if (!value || !m) return '';
  const [y, mo, d] = value.split('-').map(Number);
  const idx = mo - 1 + m;
  const year = y + Math.floor(idx / 12);
  const month = (idx % 12) + 1;
  const last = new Date(year, month, 0).getDate();
  const day = Math.min(d, last);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

export const PAYMENT_TERMS: { value: string; label: string }[] = [
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Annually', label: 'Annually' },
];

export interface FormState {
  Type: string;
  SerialNumber: string;
  E2EManufactureNumber: string;
  HMTechNumber: string;
  Status: string;
  SaleOrHire: string;
  // Company the device is sold or hired to
  CompanyID: string;
  // Hire only
  HireStartDate: string;
  ReturnDate: string;
  LicenceTypeID: string;
  ManufacturedDate: string;
  Note: string;
  // Logger / tank
  CalibrationFrequencyNumber: string;
  CalibrationFrequencyUnit: string;
  LoggerFrequency: string;
  SIMCardNumber: string;
  SIMDataNumber: string;
  AccessCode: string;
  ServiceCode: string;
  SoldDate: string;
  PaymentTerms: string;
  // Weather station
  DeviceName: string;
  Manufacture: string;
  WSStationID: string;
  // A new key or secret typed in. The stored ones are never loaded (the API
  // sends only WSApiKeySet / WSApiSecretSet), so blank keeps them.
  WSApiKey: string;
  WSApiSecret: string;
  // Calibration, licence subscription and SIM (device page)
  CalibrationEffectiveDate: string;
  LicenceStartDate: string;
  SubscriptionMonths: string;
  SIMStatus: string;
  SIMType: string;
}

export const EMPTY_FORM: FormState = {
  // Empty by default: the legacy LoggerAdd.aspx only fetches discoveries
  // *after* the user picks a Type. Defaulting to a pre-selected Type
  // (e.g. a default category) would auto-fire the TempTrack fetch on mount.
  Type: '',
  SerialNumber: '',
  E2EManufactureNumber: '',
  HMTechNumber: '',
  Status: '',
  SaleOrHire: '',
  CompanyID: '',
  HireStartDate: '',
  ReturnDate: '',
  LicenceTypeID: '',
  ManufacturedDate: '',
  Note: '',
  CalibrationFrequencyNumber: '',
  CalibrationFrequencyUnit: 'Month',
  LoggerFrequency: '',
  SIMCardNumber: '',
  SIMDataNumber: '',
  AccessCode: '',
  ServiceCode: '',
  SoldDate: '',
  PaymentTerms: '',
  DeviceName: '',
  Manufacture: '',
  WSStationID: '',
  WSApiKey: '',
  WSApiSecret: '',
  CalibrationEffectiveDate: '',
  LicenceStartDate: '',
  SubscriptionMonths: '',
  SIMStatus: '',
  SIMType: '',
};

export const isoToDateInput = (iso?: string | null): string => {
  if (!iso) return '';
  // Accepts both ISO date and datetime; strip to YYYY-MM-DD
  return iso.slice(0, 10);
};

export const dateInputToIso = (value: string): string | undefined => {
  if (!value) return undefined;
  // Send as date-only; backend stores as DateTime with 00:00:00
  return `${value}T00:00:00`;
};

export const fromLogger = (l: Logger): FormState => ({
  // In view/edit mode Type holds the loaded fine code (01-09); the UI maps
  // it to a coarse category for display via fineTypeToCoarse().
  Type: l.Type ?? '',
  SerialNumber: l.SerialNumber ?? '',
  E2EManufactureNumber: l.E2EManufactureNumber ?? '',
  HMTechNumber: l.HMTechNumber ?? '',
  Status: l.Status ?? '',
  SaleOrHire: l.SaleOrHire ?? '',
  CompanyID: l.CompanyID && l.CompanyID !== HDTECH_COMPANY_ID ? l.CompanyID : '',
  HireStartDate: isoToDateInput(l.HireStartDate),
  ReturnDate: isoToDateInput(l.ReturnDate),
  LicenceTypeID: l.LicenceTypeID ?? '',
  ManufacturedDate: isoToDateInput(l.ManufacturedDate),
  Note: l.Note ?? '',
  CalibrationFrequencyNumber:
    l.CalibrationFrequencyNumber !== undefined && l.CalibrationFrequencyNumber !== null
      ? String(l.CalibrationFrequencyNumber)
      : '',
  CalibrationFrequencyUnit: l.CalibrationFrequencyUnit ?? 'Month',
  LoggerFrequency: l.LoggerFrequency ?? '',
  SIMCardNumber: l.SIMCardNumber ?? '',
  SIMDataNumber: l.SIMDataNumber ?? '',
  AccessCode: l.AccessCode ?? '',
  ServiceCode: l.ServiceCode ?? '',
  SoldDate: isoToDateInput(l.SoldDate),
  PaymentTerms: l.PaymentTerms ?? '',
  DeviceName: l.DeviceName ?? '',
  Manufacture: l.Manufacture ?? '',
  WSStationID: l.WSStationID ?? '',
  WSApiKey: '',
  WSApiSecret: '',
  CalibrationEffectiveDate: isoToDateInput(l.CalibrationEffectiveDate),
  LicenceStartDate: isoToDateInput(l.LicenceStartDate),
  SubscriptionMonths: l.SubscriptionMonths ? String(l.SubscriptionMonths) : '',
  SIMStatus: l.SIMStatus ?? '',
  SIMType: l.SIMType ?? '',
});

// Manual create path: `f.Type` holds the coarse category. The fine
// LoggerInfo.Type code is derived from coarse + serial prefix.
export const toCreatePayload = (f: FormState): LoggerCreate => {
  const coarse = f.Type;
  // For tank/weather (no discovery picker) the serial isn't entered
  // separately — legacy stores the Hardware Number as the serial. For
  // logger categories the serial is filled from the picked discovery.
  const serial = (f.SerialNumber || f.HMTechNumber).trim();
  const base: LoggerCreate = {
    SerialNumber: serial,
    Type: deriveFineType(coarse, serial) || undefined,
    HMTechNumber: f.HMTechNumber.trim() || undefined,
    Status: f.Status || undefined,
    Note: f.Note || undefined,
    SaleOrHire: f.SaleOrHire || undefined,
    CompanyID: isHire(f.SaleOrHire) ? undefined : f.CompanyID || undefined,
    LicenceTypeID: f.LicenceTypeID || undefined,
    ManufacturedDate: dateInputToIso(f.ManufacturedDate),
    E2EManufactureNumber: f.E2EManufactureNumber || undefined,
  };
  if (hasCalibrationRow(coarse)) {
    base.CalibrationFrequencyNumber = f.CalibrationFrequencyNumber
      ? Number(f.CalibrationFrequencyNumber)
      : undefined;
    // CalibrationFrequencyUnit is varchar(1) in the legacy DB. The legacy
    // LoggerAdd/Edit pages never write it, so we don't either — leaving it
    // alone avoids a "string would be truncated" error.
  }
  if (hasLoggerFields(coarse)) {
    base.SIMCardNumber = f.SIMCardNumber || undefined;
    base.SIMDataNumber = f.SIMDataNumber || undefined;
    base.AccessCode = f.AccessCode || undefined;
    base.ServiceCode = f.ServiceCode || undefined;
    base.LoggerFrequency = f.LoggerFrequency || undefined;
    base.PaymentTerms = isHire(f.SaleOrHire) ? f.PaymentTerms || undefined : undefined;
  }
  if (hasWeatherFields(coarse)) {
    base.DeviceName = f.DeviceName || undefined;
    base.Manufacture = f.Manufacture || undefined;
    base.WSStationID = f.WSStationID || undefined;
    base.WSApiKey = f.WSApiKey || undefined;
    base.WSApiSecret = f.WSApiSecret || undefined;
  }
  return base;
};

// Update path (device page): `f.Type` holds the loaded fine code, which the
// form never changes. Fields follow the spreadsheet's per-type rules. The
// logging frequency is not sent: it has its own endpoint, which can also
// push it to the logger.
export const toUpdatePayload = (f: FormState): LoggerUpdate => {
  const fine = f.Type;
  const payload: LoggerUpdate = {
    SerialNumber: f.SerialNumber.trim() || undefined,
    Type: f.Type || undefined,
    HMTechNumber: f.HMTechNumber.trim() || undefined,
    Status: f.Status || undefined,
    Note: f.Note,
    SaleOrHire: f.SaleOrHire || undefined,
    // "" unassigns it server-side
    CompanyID: f.CompanyID,
    // Always sent: "" clears the licence server-side
    LicenceTypeID: f.LicenceTypeID,
    ManufacturedDate: dateInputToIso(f.ManufacturedDate),
    E2EManufactureNumber: f.E2EManufactureNumber || undefined,
    LicenceStartDate: dateInputToIso(f.LicenceStartDate),
    // 0 clears the subscription
    SubscriptionMonths: f.SubscriptionMonths ? Number(f.SubscriptionMonths) : 0,
  };
  if (isScheduledLoggerType(fine) || isTankType(fine)) {
    // Tank stores its default temperature in this column
    payload.CalibrationFrequencyNumber = f.CalibrationFrequencyNumber
      ? Number(f.CalibrationFrequencyNumber)
      : undefined;
  }
  if (isScheduledLoggerType(fine)) {
    payload.CalibrationEffectiveDate = dateInputToIso(f.CalibrationEffectiveDate);
  }
  if (isAgentType(fine)) {
    payload.SIMCardNumber = f.SIMCardNumber;
    payload.SIMDataNumber = f.SIMDataNumber;
    payload.SIMStatus = f.SIMStatus;
    payload.SIMType = f.SIMType;
    payload.AccessCode = f.AccessCode;
    payload.ServiceCode = f.ServiceCode;
  }
  if (hasLoggerFields(fineTypeToCoarse(fine))) {
    payload.PaymentTerms = isHire(f.SaleOrHire) ? f.PaymentTerms || undefined : undefined;
  }
  if (isWeatherType(fine)) {
    payload.DeviceName = f.DeviceName;
    payload.Manufacture = f.Manufacture;
    payload.WSStationID = f.WSStationID;
    // Only a newly typed key or secret: left out keeps the stored one.
    if (f.WSApiKey.trim()) payload.WSApiKey = f.WSApiKey;
    if (f.WSApiSecret.trim()) payload.WSApiSecret = f.WSApiSecret;
  }
  if (f.SoldDate) payload.SoldDate = dateInputToIso(f.SoldDate);
  if (isHire(f.SaleOrHire) && f.CompanyID) {
    payload.HireStartDate = dateInputToIso(f.HireStartDate);
    payload.ReturnDate = dateInputToIso(f.ReturnDate);
  }
  return payload;
};

/**
 * Build the payload for `/hardware/temptrack/register`. Used when the user
 * selected a TempTrack discovery from the dropdown — the backend promotes
 * the discovery on TempTrack and persists LoggerInfo atomically using
 * these fields. SerialNumber + E2EManufactureNumber are intentionally
 * omitted: the backend reads them from the TempTrack discovery itself.
 */
export const toRegisterPayload = (
  f: FormState,
  discovery: TemptrackDiscoveryItem,
  deviceType: TemptrackDeviceType,
): RegisterLoggerPayload => {
  const coarse = f.Type;
  const payload: RegisterLoggerPayload = {
    temptrack_id: discovery.temptrack_id,
    device_type: deviceType,
    hm_tech_number: f.HMTechNumber.trim(),
    // For ConXenviro the user's dropdown choice wins regardless of serial
    // prefix; otherwise the backend derives the fine Type code from the
    // device serial prefix (mirrors legacy CreateLogger).
    type_code: coarse === HW_CONXENVIRO ? '10' : undefined,
    status: f.Status || undefined,
    note: f.Note || undefined,
    sale_or_hire: f.SaleOrHire || undefined,
    // Without this the backend falls back to the signed-in user's company
    company_id: (!isHire(f.SaleOrHire) && f.CompanyID) || HDTECH_COMPANY_ID,
    manufactured_date: dateInputToIso(f.ManufacturedDate),
    payment_terms: isHire(f.SaleOrHire) ? f.PaymentTerms || undefined : undefined,
  };
  if (hasCalibrationRow(coarse)) {
    payload.calibration_frequency_number = f.CalibrationFrequencyNumber
      ? Number(f.CalibrationFrequencyNumber)
      : undefined;
  }
  if (hasLoggerFields(coarse)) {
    payload.sim_card_number = f.SIMCardNumber || undefined;
    payload.sim_data_number = f.SIMDataNumber || undefined;
    payload.access_code = f.AccessCode || undefined;
    payload.service_code = f.ServiceCode || undefined;
    payload.logger_frequency = f.LoggerFrequency || undefined;
  }
  if (hasWeatherFields(coarse)) {
    payload.device_name = f.DeviceName || undefined;
    payload.manufacture = f.Manufacture || undefined;
    payload.ws_station_id = f.WSStationID || undefined;
    payload.ws_api_key = f.WSApiKey || undefined;
    payload.ws_api_secret = f.WSApiSecret || undefined;
  }
  return payload;
};
